import React, { useState, useEffect } from 'react'
import { Eye, EyeOff, Loader2, Moon, Sun } from 'lucide-react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '@/context/ThemeContext'
import Cookies from 'js-cookie'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { googleSignInStartUrl, microsoftSignInStartUrl } from '@/features/auth/services/authApi'
import { googleSignInErrorCopy } from '@/features/auth/lib/googleSignInErrorCopy'
import { microsoftSignInErrorCopy } from '@/features/auth/lib/microsoftSignInErrorCopy'

type Mode = 'login' | 'signup' | 'reset'

export const LoginPage: React.FC = () => {
  const [mode, setMode] = useState<Mode>('login')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)

  const { isDarkMode, setIsDarkMode, currentTheme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { login, register, resetPassword } = useAuth()

  useEffect(() => {
    const token = Cookies.get('token')
    if (token) {
      navigate('/dashboard', { replace: true })
    }
  }, [navigate])

  // Two distinct sources for a failed Google/Microsoft sign-in: the OAuth
  // callback redirects here with a `reason` code (?googleAuth=error&reason=...
  // or ?microsoftAuth=error&reason=...), mapped to copy via
  // googleSignInErrorCopy/microsoftSignInErrorCopy; the session-exchange step
  // on /login/google|microsoft/callback instead hands back an already
  // user-facing message through navigation state (see
  // GoogleCallbackPage.tsx/MicrosoftCallbackPage.tsx).
  useEffect(() => {
    const state = location.state as { googleSignInError?: string; microsoftSignInError?: string } | null
    const stateError = state?.googleSignInError ?? state?.microsoftSignInError
    if (stateError) {
      setError(stateError)
      navigate(location.pathname, { replace: true, state: null })
      return
    }

    if (searchParams.get('googleAuth') === 'error') {
      setError(googleSignInErrorCopy(searchParams.get('reason')))
      const next = new URLSearchParams(searchParams)
      next.delete('googleAuth')
      next.delete('reason')
      setSearchParams(next, { replace: true })
      return
    }

    if (searchParams.get('microsoftAuth') === 'error') {
      setError(microsoftSignInErrorCopy(searchParams.get('reason')))
      const next = new URLSearchParams(searchParams)
      next.delete('microsoftAuth')
      next.delete('reason')
      setSearchParams(next, { replace: true })
    }
    // Only ever consumed once on arrival — re-running on every searchParams
    // change would loop, since this effect itself clears them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const validateEmail = (emailToCheck: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(emailToCheck)
  }

  const validateLogin = () => {
    if (!email.trim() || !validateEmail(email.trim())) {
      setError('Please enter a valid email address.')
      return false
    }

    if (!password.trim()) {
      setError('Password is required.')
      return false
    }

    setError('')
    return true
  }

  const validateSignup = () => {
    if (!name.trim()) {
      setError('Full name is required.')
      return false
    }

    if (name.trim().length < 3) {
      setError('Name must be at least 3 characters long.')
      return false
    }

    if (!email.trim() || !validateEmail(email.trim())) {
      setError('Please enter a valid email address.')
      return false
    }

    if (!password.trim()) {
      setError('Password is required.')
      return false
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return false
    }

    if (!/[A-Za-z]/.test(password)) {
      setError('Password must include at least one letter.')
      return false
    }

    if (!/\d/.test(password)) {
      setError('Password must include at least one number.')
      return false
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return false
    }

    setError('')
    return true
  }

  const resetForm = () => {
    setError('')
    setSuccess('')
  }

  const validateReset = () => {
    if (!email.trim() || !validateEmail(email.trim())) {
      setError('Please enter a valid email address.')
      return false
    }
    if (!password.trim()) {
      setError('Password is required.')
      return false
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return false
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return false
    }
    setError('')
    return true
  }

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault()
    setLoading(true)
    resetForm()

    if (!validateLogin()) {
      setLoading(false)
      return
    }

    const loginData = {
      email: email.trim(),
      password,
    }

    try {
      await login(loginData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to sign in. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleReset = async (event: React.FormEvent) => {
    event.preventDefault()
    setLoading(true)
    resetForm()

    if (!validateReset()) {
      setLoading(false)
      return
    }

    try {
      await resetPassword({ email: email.trim(), password, confirm_password: confirmPassword })
      setSuccess('Password reset successfully. You can now sign in.')
      setPassword('')
      setConfirmPassword('')
      setTimeout(() => setMode('login'), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to reset password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSignUp = async (event: React.FormEvent) => {
    event.preventDefault()
    setLoading(true)
    resetForm()

    if (!validateSignup()) {
      setLoading(false)
      return
    }

    const signUpData = {
      name: name.trim(),
      email: email.trim(),
      password,
      confirm_password: confirmPassword,
    }

    try {
      await register(signUpData)
      navigate('/onboarding', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to register. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const switchMode = (next: Mode) => {
    setMode(next)
    resetForm()
    setName('')
    setPassword('')
    setConfirmPassword('')
  }

  const heroTitle =
    mode === 'signup' ? 'Create your account' :
    mode === 'reset'  ? 'Reset your password' :
                        'Welcome back'
  const heroSubtitle =
    mode === 'signup' ? 'Join Zeph.AI and start generating better outreach, faster.' :
    mode === 'reset'  ? 'Enter your email and choose a new password.' :
                        'Sign in to your dashboard and keep your pipeline moving.'
  const submitLabel =
    mode === 'signup' ? 'Create account' :
    mode === 'reset'  ? 'Reset password' :
                        'Sign in'

  return (
    <div
      className="min-h-screen transition-colors duration-300"
      style={{ backgroundColor: currentTheme.primary }}
    >
      <div className="relative mx-auto flex min-h-screen max-w-[1480px] items-center px-4 py-6 sm:px-6 xl:px-8">
        <button
          type="button"
          onClick={() => setIsDarkMode(!isDarkMode)}
          className="absolute right-4 top-4 z-10 inline-flex items-center justify-center rounded-full border p-3 shadow-sm transition"
          style={{
            backgroundColor: currentTheme.secondary,
            color: currentTheme.text,
            borderColor: currentTheme.border,
          }}
          aria-label="Toggle theme"
        >
          {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <div className="mt-14 grid w-full gap-8 sm:mt-16 xl:mt-0 xl:grid-cols-[1.1fr_0.9fr] items-stretch">
          <section
            className="overflow-hidden rounded-none border-0 shadow-none xl:rounded-2xl xl:border xl:shadow-[0_32px_80px_rgba(15,23,42,0.08)]"
            style={{
              backgroundColor: currentTheme.secondary,
              borderColor: currentTheme.border,
            }}
          >
            <div className="p-6 sm:p-9 xl:p-10">
              <div className="mb-8 flex items-center gap-3 xl:hidden">
                <img
                  src={isDarkMode ? '/Zeph-White-Logo.png' : '/Zeph-Black-Logo.png'}
                  alt="Zeph AI Logo"
                  className="h-8 w-8 object-contain"
                />
                <span className="text-base font-semibold" style={{ color: currentTheme.text }}>
                  Zeph AI
                </span>
              </div>

              <div className="mb-6 flex flex-col gap-3">
                <div>
                  <h1
                    className="text-2xl font-semibold sm:text-3xl xl:text-4xl"
                    style={{ color: currentTheme.text }}
                  >
                    {heroTitle}
                  </h1>
                  <p
                    className="mt-2 max-w-2xl text-sm leading-6 sm:text-base sm:leading-7"
                    style={{ color: currentTheme.secondrytext }}
                  >
                    {heroSubtitle}
                  </p>
                </div>
              </div>

              {success && (
                <p
                  className="mb-5 text-sm font-medium"
                  style={{ color: currentTheme.successText }}
                >
                  {success}
                </p>
              )}

              {error && (
                <p
                  className="mb-5 text-sm font-medium"
                  style={{ color: currentTheme.errorText }}
                >
                  {error}
                </p>
              )}

              <form
                onSubmit={mode === 'signup' ? handleSignUp : mode === 'reset' ? handleReset : handleLogin}
                className="space-y-4"
              >
                <div className="space-y-4">
                  {mode === 'signup' && (
                    <div>
                      <label
                        className="block text-sm font-semibold mb-2"
                        style={{ color: currentTheme.text }}
                      >
                        Full name
                      </label>
                      <input
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value)
                          setError('')
                        }}
                        placeholder="John Doe"
                        className="w-full rounded-xl border px-4 py-3 text-sm outline-none transition"
                        style={{
                          backgroundColor: currentTheme.inputBackground,
                          color: currentTheme.text,
                          borderColor: currentTheme.border,
                        }}
                      />
                    </div>
                  )}

                  <div>
                    <label
                      className="block text-sm font-semibold mb-2"
                      style={{ color: currentTheme.text }}
                    >
                      Email address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value)
                        setError('')
                      }}
                      placeholder="you@example.com"
                      className="w-full rounded-xl border px-4 py-3 text-sm outline-none transition"
                      style={{
                        backgroundColor: currentTheme.inputBackground,
                        color: currentTheme.text,
                        borderColor: currentTheme.border,
                      }}
                    />
                  </div>
                </div>

                <div className={mode === 'signup' ? 'grid gap-5 sm:grid-cols-2' : ''}>
                  <div>
                    <label
                      className="block text-sm font-semibold mb-2"
                      style={{ color: currentTheme.text }}
                    >
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value)
                          setError('')
                        }}
                        placeholder="••••••••"
                        className="w-full rounded-xl border px-4 py-3 pr-12 text-sm outline-none transition"
                        style={{
                          backgroundColor: currentTheme.inputBackground,
                          color: currentTheme.text,
                          borderColor: currentTheme.border,
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full transition"
                        style={{
                          color: currentTheme.secondrytext,
                          backgroundColor: currentTheme.secondary,
                        }}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {(mode === 'signup' || mode === 'reset') && (
                    <div>
                      <label
                        className="block text-sm font-semibold mb-2"
                        style={{ color: currentTheme.text }}
                      >
                        Confirm password
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmPassword(e.target.value)
                            setError('')
                          }}
                          placeholder="••••••••"
                          className="w-full rounded-xl border px-4 py-3 pr-12 text-sm outline-none transition"
                          style={{
                            backgroundColor: currentTheme.inputBackground,
                            color: currentTheme.text,
                            borderColor: currentTheme.border,
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full transition"
                          style={{
                            color: currentTheme.secondrytext,
                            backgroundColor: currentTheme.secondary,
                          }}
                        >
                          {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
                  style={{
                    backgroundColor: currentTheme.sendButtonPrimary,
                    color: currentTheme.buttonText,
                    border: `1px solid ${currentTheme.border}`,
                  }}
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                  <span>{loading ? 'Processing...' : submitLabel}</span>
                </button>
              </form>

              {mode !== 'reset' && (
                <>
                  <div className="my-5 flex items-center gap-3">
                    <span className="h-px flex-1" style={{ backgroundColor: currentTheme.border }} />
                    <span className="text-xs font-medium" style={{ color: currentTheme.secondrytext }}>
                      or
                    </span>
                    <span className="h-px flex-1" style={{ backgroundColor: currentTheme.border }} />
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      window.location.href = googleSignInStartUrl
                    }}
                    className="flex w-full items-center justify-center gap-3 rounded-xl px-5 py-3 text-sm font-semibold transition"
                    style={{
                      backgroundColor: currentTheme.inputBackground,
                      color: currentTheme.text,
                      border: `1px solid ${currentTheme.border}`,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 6 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" />
                      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.9 18.9 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 6 29.6 4 24 4c-7.5 0-13.9 4.2-17.2 10.4z" />
                      <path fill="#4CAF50" d="M24 44c5.5 0 10.4-1.9 14.3-5.1l-6.6-5.6c-2 1.5-4.6 2.6-7.7 2.6-5.2 0-9.6-3.3-11.3-7.9l-6.6 5.1C9.9 39.7 16.4 44 24 44z" />
                      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4 5.6l6.6 5.6C40.8 36.6 44 31 44 24c0-1.2-.1-2.4-.4-3.5z" />
                    </svg>
                    <span>Continue with Google</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      window.location.href = microsoftSignInStartUrl
                    }}
                    className="mt-3 flex w-full items-center justify-center gap-3 rounded-xl px-5 py-3 text-sm font-semibold transition"
                    style={{
                      backgroundColor: currentTheme.inputBackground,
                      color: currentTheme.text,
                      border: `1px solid ${currentTheme.border}`,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
                      <path fill="#F25022" d="M1 1h10v10H1z" />
                      <path fill="#00A4EF" d="M1 12h10v10H1z" />
                      <path fill="#7FBA00" d="M12 1h10v10H12z" />
                      <path fill="#FFB900" d="M12 12h10v10H12z" />
                    </svg>
                    <span>Continue with Microsoft</span>
                  </button>
                </>
              )}

              <div className="mt-4 flex flex-col gap-3 text-center text-sm">
                {mode === 'login' && (
                  <p style={{ color: currentTheme.secondrytext }}>
                    <button
                      type="button"
                      onClick={() => switchMode('reset')}
                      className="font-semibold underline transition"
                      style={{ color: currentTheme.text }}
                    >
                      Forgot password?
                    </button>
                  </p>
                )}
                <p style={{ color: currentTheme.secondrytext }}>
                  {mode === 'signup' ? 'Already have an account?' : mode === 'reset' ? 'Remembered it?' : 'New to Zeph.AI?'}
                  <button
                    type="button"
                    onClick={() => switchMode(mode === 'signup' ? 'login' : mode === 'reset' ? 'login' : 'signup')}
                    className="ml-2 font-semibold underline transition"
                    style={{ color: currentTheme.text }}
                  >
                    {mode === 'signup' ? 'Log in' : mode === 'reset' ? 'Back to sign in' : 'Create account'}
                  </button>
                </p>
              </div>
            </div>
          </section>

          <aside
            className="relative hidden overflow-hidden rounded-2xl shadow-[0_32px_80px_rgba(15,23,42,0.25)] xl:block"
            style={{ backgroundColor: currentTheme.secondary }}
          >
            <img
              src="https://media4.giphy.com/media/DIouyQ9xdybpAmRUVI/giphy.webp"
              alt="Animated growth illustration"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0" style={{ backgroundColor: 'rgba(15,23,42,0.72)' }} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="flex items-center gap-4">
                <img
                  src="/Zeph-White-Logo.png"
                  alt="Zeph AI Logo"
                  className="w-16 h-16 object-contain"
                />
                <h2 className="text-4xl font-semibold text-white">Zeph AI</h2>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
